import { useEffect, useRef, useState } from 'react';

export function prefersReducedMotion(): boolean {
  return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
}

/**
 * True once the element has scrolled into view, and stays true — the reveals
 * on the welcome play once, they do not replay every time you scroll past.
 */
export function useInView<T extends Element>(threshold = 0.25): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || seen) return;
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [seen, threshold]);

  return [ref, seen];
}

/** Counts from zero to `target` once `active` turns true. */
export function useCountFromZero(target: number, active: boolean, ms = 1400): number {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) return;
    if (prefersReducedMotion() || document.hidden) {
      setValue(target);
      return;
    }
    let frame = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / ms);
      setValue(Math.round(target * (1 - (1 - p) ** 4)));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, active, ms]);

  return value;
}

/** Steps through `0..count-1` on an interval, for cycling highlights. */
export function useTicker(count: number, ms: number, active = true): number {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!active || count < 2 || prefersReducedMotion()) return;
    const timer = window.setInterval(() => setIndex(current => (current + 1) % count), ms);
    return () => window.clearInterval(timer);
  }, [count, ms, active]);

  return index;
}
